import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  MapPin,
  UserRound,
} from 'lucide-react';

const PRIORITY = {
  p0: {
    label: 'Crítica',
    helper: 'Faça primeiro',
    badge: 'bg-red-100 text-red-700 border-red-200',
    border: 'border-l-red-500',
  },
  p1: {
    label: 'Alta',
    helper: 'Próxima ação',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    border: 'border-l-amber-500',
  },
  p2: {
    label: 'Melhoria',
    helper: 'Pode aguardar',
    badge: 'bg-sky-100 text-sky-700 border-sky-200',
    border: 'border-l-sky-500',
  },
};

const OWNER_LABELS = {
  ops_panel: 'Operação no painel',
  engineering: 'Engenharia',
};

function priorityFor(value) {
  return PRIORITY[value?.toLowerCase()] || {
    label: 'Sem prioridade',
    helper: 'Avaliar',
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    border: 'border-l-slate-400',
  };
}

function ActionCard({ action }) {
  const priority = priorityFor(action.priority);
  const path = (action.where || '').split(/\s*>\s*/).filter(Boolean);

  return (
    <article
      className={`overflow-hidden rounded-2xl border border-surface-border border-l-4 bg-white shadow-card ${priority.border}`}
    >
      <div className="border-b border-slate-100 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${priority.badge}`}
          >
            {action.priority?.toUpperCase()} · {priority.label}
          </span>
          {action.id && (
            <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              Ação {action.id}
            </span>
          )}
          <span className="text-xs text-content-muted">· {priority.helper}</span>
        </div>
        <h3 className="mt-3 text-lg font-bold leading-snug text-content-primary sm:text-xl">
          {action.title || 'Ação recomendada'}
        </h3>
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-content-secondary">
          <UserRound className="h-4 w-4 text-content-muted" aria-hidden />
          <span className="font-medium">Responsável:</span>
          {OWNER_LABELS[action.owner_role] || action.owner_role || 'A definir'}
        </div>
      </div>

      <div className="space-y-6 px-5 py-5 sm:px-6">
        {path.length > 0 && (
          <section>
            <h4 className="flex items-center gap-2 text-sm font-bold text-content-primary">
              <MapPin className="h-4 w-4 text-forge-600" aria-hidden />
              Onde fazer no painel
            </h4>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-xl border border-forge-100 bg-forge-50 px-4 py-3">
              {path.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {index > 0 && (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-forge-400" aria-hidden />
                  )}
                  <span className="text-sm font-medium text-forge-800">{part}</span>
                </React.Fragment>
              ))}
            </div>
          </section>
        )}

        {action.proposed_change && (
          <section>
            <h4 className="text-sm font-bold text-content-primary">O que alterar</h4>
            <div className="mt-2 rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-4 text-sm leading-6 text-content-secondary">
              {action.proposed_change}
            </div>
          </section>
        )}

        {Array.isArray(action.steps) && action.steps.length > 0 && (
          <section>
            <h4 className="text-sm font-bold text-content-primary">Passo a passo</h4>
            <ol className="mt-3 space-y-3">
              {action.steps.map((step, index) => (
                <li key={`${step}-${index}`} className="flex gap-3 text-sm text-content-secondary">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forge-600 text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="pt-0.5 leading-5">{step}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          {action.expected_effect && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
              <p className="flex items-center gap-2 text-sm font-bold text-emerald-900">
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                Resultado esperado
              </p>
              <p className="mt-2 text-sm leading-5 text-emerald-900/80">
                {action.expected_effect}
              </p>
            </div>
          )}
          {action.validation && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4">
              <p className="flex items-center gap-2 text-sm font-bold text-blue-900">
                <ClipboardCheck className="h-4 w-4" aria-hidden />
                Como conferir
              </p>
              <p className="mt-2 text-sm leading-5 text-blue-900/80">{action.validation}</p>
            </div>
          )}
        </div>

        {(action.current_state ||
          (Array.isArray(action.evidence_refs) && action.evidence_refs.length > 0)) && (
          <details className="group rounded-xl border border-slate-200 bg-slate-50">
            <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-content-secondary">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-content-muted" aria-hidden />
                Por que esta ação foi recomendada?
              </span>
            </summary>
            <div className="border-t border-slate-200 px-4 py-3 text-sm leading-6 text-content-muted">
              {action.current_state && <p>{action.current_state}</p>}
              {Array.isArray(action.evidence_refs) && action.evidence_refs.length > 0 && (
                <p className="mt-2">
                  <span className="font-semibold text-content-secondary">Conversas analisadas: </span>
                  {action.evidence_refs.join(', ')}. Esses códigos são referências internas, não
                  etapas a executar.
                </p>
              )}
            </div>
          </details>
        )}
      </div>
    </article>
  );
}

function ActionPlanCards({ actions }) {
  if (!Array.isArray(actions) || actions.length === 0) return null;

  const sortedActions = [...actions].sort((a, b) => {
    const rank = { p0: 0, p1: 1, p2: 2 };
    return (rank[a.priority] ?? 3) - (rank[b.priority] ?? 3);
  });
  const criticalCount = actions.filter((action) => action.priority === 'p0').length;

  return (
    <section className="mt-10 border-t border-slate-200 pt-8" aria-labelledby="action-plan-title">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-forge-600">
            Próximos passos
          </p>
          <h2 id="action-plan-title" className="mt-1 text-2xl font-bold text-content-primary">
            Plano de ação operacional
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-content-muted">
            Siga os itens na ordem de prioridade. Cada card informa quem executa, onde alterar e
            como confirmar o resultado.
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-medium text-content-secondary">
            {actions.length} {actions.length === 1 ? 'ação' : 'ações'}
          </span>
          {criticalCount > 0 && (
            <span className="rounded-full bg-red-100 px-3 py-1.5 font-bold text-red-700">
              {criticalCount} {criticalCount === 1 ? 'crítica' : 'críticas'}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {sortedActions.map((action, index) => (
          <ActionCard key={action.id || `${action.title}-${index}`} action={action} />
        ))}
      </div>
    </section>
  );
}

export default ActionPlanCards;
